/**
 * TenantSelectionPage - Join existing org or create new (after payment in signup flow)
 * MODULE: core/auth/frontend/pages/TenantSelectionPage.tsx
 * PURPOSE: Tenant join/create during signup; requires payment or bypass voucher proof server-side
 * RECENT: 2026-06-13 [2.2.1383] — Payment gate via sessionStorage proof; passes checkout_session_id / bypass_voucher_code to complete-registration
 */
import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, TextInput, Label, Button, Spinner } from 'flowbite-react';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import {
  persistSignupSessionAndRedirect,
  markSignupWelcomeEmailPending,
  loadSignupPendingState,
  isSignupAccessGranted,
  getSignupPaymentProof,
  getSignupBypassVoucher,
  clearSignupPendingState,
} from '../utils/persistSignupSession';
import { TenantRadioCard } from '../components/TenantRadioCard';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;

interface TenantResolutionState {
  action: string;
  existing_tenant?: { id: string; name: string; domain: string; logo_url?: string };
}

export function TenantSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as {
    userId?: string;
    tenantResolution?: TenantResolutionState;
    email?: string;
  } | undefined;

  const pendingSignup = loadSignupPendingState();
  const userId = state?.userId ?? pendingSignup?.userId;
  const signupEmail = state?.email ?? pendingSignup?.email;
  const resolution = state?.tenantResolution ?? pendingSignup?.tenantResolution;

  const [choice, setChoice] = useState<'join' | 'create'>(
    resolution?.existing_tenant ? 'join' : 'create'
  );
  const [organizationName, setOrganizationName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId && !resolution) {
      navigate('/auth?tab=register', { replace: true });
      return;
    }
    if (userId && !isSignupAccessGranted()) {
      navigate('/subscription-setup', { replace: true });
    }
  }, [userId, resolution, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    if (choice === 'create' && (!organizationName || organizationName.trim().length < 2)) {
      setError('Organization name must be at least 2 characters.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const paymentProof = getSignupPaymentProof();
      const bypassCode = getSignupBypassVoucher();
      const res = await fetch(`${API_BASE}/complete-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          tenant_choice: choice,
          tenant_id: choice === 'join' && resolution?.existing_tenant ? resolution.existing_tenant.id : undefined,
          new_tenant_name: choice === 'create' ? organizationName.trim() : undefined,
          checkout_session_id: paymentProof?.checkoutSessionId,
          bypass_voucher_code: bypassCode || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        let msg = 'Failed to complete registration';
        if (typeof data.detail === 'string') msg = data.detail;
        else if (Array.isArray(data.detail) && data.detail.length > 0) {
          const first = data.detail[0];
          msg =
            first && typeof first === 'object' && 'msg' in first
              ? String((first as { msg: string }).msg)
              : String(first);
        }
        throw new Error(msg);
      }

      clearSignupPendingState();

      if (data.access_token && data.refresh_token) {
        if (signupEmail) {
          markSignupWelcomeEmailPending(signupEmail);
        }
        persistSignupSessionAndRedirect(
          {
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            tenant_id: data.tenant_id,
          },
          '/mfa-setup'
        );
        return;
      }

      navigate('/mfa-setup');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const existingTenant = resolution?.existing_tenant;
  const showTwoOptions = resolution?.action === 'PROMPT_JOIN_OR_CREATE' && existingTenant;

  if (!userId) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="max-w-md w-full mx-auto p-6">
        <h2 className="text-xl font-semibold text-gray-900">Join or create an organization</h2>
        <p className="text-sm text-gray-600 mt-1 mb-6">
          {showTwoOptions ? 'Choose to join your team or create a new organization.' : 'Enter your organization name.'}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}
          {showTwoOptions && (
            <div className="space-y-3">
              <TenantRadioCard
                tenant={{
                  id: existingTenant.id,
                  name: existingTenant.name,
                  domain: existingTenant.domain,
                  logo_url: existingTenant.logo_url,
                }}
                selected={choice === 'join'}
                onSelect={() => setChoice('join')}
                type="join"
              />
              <TenantRadioCard
                tenant={{
                  id: 'create',
                  name: 'Create new organization',
                  domain: '',
                }}
                selected={choice === 'create'}
                onSelect={() => setChoice('create')}
                type="create"
              />
            </div>
          )}
          {choice === 'create' && (
            <div>
              <Label htmlFor="orgName">Organization name</Label>
              <TextInput
                id="orgName"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Enter your company name"
                disabled={submitting}
                className="mt-1"
              />
            </div>
          )}
          <Button type="submit" className="w-full" disabled={submitting || (choice === 'create' && organizationName.trim().length < 2)}>
            {submitting ? <><Spinner size="sm" className="mr-2" /> Continuing...</> : 'Continue'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
